/** @module Auth0PostLoginHandlerHelper */
/* eslint-disable import/extensions */
import Auth0HandlerRegistry from './auth0-post-login-handler-config.mjs';
import Auth0PostLoginHandlerDevDataDump from './implementations/auth0-post-login-handler-dev-data-dump.mjs';
import logger from '../../../utils/logging/auth0-logger.mjs';

class Auth0PostLoginHandlerHelper {
  constructor(options) {
    this._handlerRegistry = Auth0HandlerRegistry.registeredHandlers.map((h) => {
      return new h.HandlerClass({
        name: h.handlerName,
        description: h.handlerDescription,
        isDefault: h.isDefault,
        crossDomainRedirect: h.crossDomainRedirect,
        ...options,
      });
    });
    this._devDataDumpHandler = new Auth0PostLoginHandlerDevDataDump({
      name: 'Dev Data Dump Handler', description: 'Used to inspect data that Auth0PostLoginHandlerBase have available to them', ...options,
    });
    const defaultHandlers = this._handlerRegistry.filter(h => h.isDefault);
    if (!defaultHandlers || !defaultHandlers.length) {
      throw new Error('The login handler helper found no default handlers, specify `isDefault: true` for the default.');
    }
    if (defaultHandlers.length > 1) {
      throw new Error('The login handler helper found multiple default handlers, specify `isDefault: true` for just the default.');
    }
    this._defaultHandler = defaultHandlers.pop();

    console.log(`Auth0PostLoginHandlerHelper initialized with ${this._handlerRegistry.length} handlers and "${this._defaultHandler.name}" as the default...`);
  }

  get handlers() {
    return this._handlerRegistry;
  }

  get defaultHandler() {
    return this._defaultHandler;
  }

  /**
   * @function
   * @description This function determines, based on user and app state whether or not this handler is to be chosen as the post login handler. Only one will be chose (first one to return true here)
   * @param {object} loginData Data about the just logged in user
   * @param {object} loginData.user The auth0 user that logged in
   * @param {object} loginData.appState The app state prior to loginWithRedirect()
   * @param {object} [loginData.idpData] If SSO was used, this will hold an Auth0IdentityProvider representing the provider used for login
   * @param {string} [loginData.appState.redirectOverride] The URL to use for post-auth redirect (instead of going to SSHome)
   * @returns {Auth0PostLoginHandlerBase} The Auth0PostLoginHandlerBase object that should handle the post-login actions
   */
  selectHandler = async (loginData) => {
    // for dev, check to see if dev data dump handler is active
    const useDataDump = await this._devDataDumpHandler.shouldHandle();
    if (useDataDump) {
      logger.warn('DEV DATA DUMP IS ACTIVE, MAKE SURE TO DISABLE WHEN DONE');
      return this._devDataDumpHandler;
    }

    const found = await Promise.all(this.handlers.map(async (h) => {
      const shouldRun = await h.shouldHandle(loginData);
      return { handler: h, shouldRun };
    }));

    const filteredHandlers = found
      .filter(result => result.shouldRun)
      .map(result => result.handler);

    if (!filteredHandlers || !filteredHandlers.length) {
      return this.defaultHandler;
    }
    if (filteredHandlers.length > 1) {
      const handlerNames = filteredHandlers.map(h => h.name).join(', ');
      const selectedName = filteredHandlers[0].name;
      console.warn(`[Auth0PostLoginHandlerHelper] Multiple handlers matched: [${handlerNames}] — using "${selectedName}" (first registered wins)`);
      return filteredHandlers[0];
    }
    return filteredHandlers.pop();
  };

  /**
   * @function
   * @description This function determines, based on user and app state whether or not this handler is to be chosen as the post login handler. Only one will be chose (first one to return true here) and then runs its actions.
   * @param {object} loginData Data about the just logged in user
   * @param {object} loginData.user The auth0 user that logged in
   * @param {object} loginData.appState The app state prior to loginWithRedirect()
   * @param {object} [loginData.idpData] If SSO was used, this will hold an Auth0IdentityProvider representing the provider used for login
   * @param {string} [loginData.appState.redirectOverride] The URL to use for post-auth redirect (instead of going to SSHome)
   */
  selectAndRunPostLoginActions = async (loginData) => {
    const theHandler = await this.selectHandler(loginData);
    console.log(`Auth0PostLoginHandlerHelper selected the "${theHandler.name}" handler...`);
    await theHandler.run(loginData);
    if (theHandler.crossDomainRedirect) {
      // Timing: run() has queued the navigation (location.href assignment / form.submit()) but
      // the browser cannot commit it until this task's microtasks drain — the cleanup below
      // runs as one of those microtasks, so its cookie/localStorage writes always land before
      // the page unloads. CONTRACT for handlers declared crossDomainRedirect: run() must
      // resolve immediately after initiating its navigation — if it awaits a macrotask
      // (fetch/timer) or never resolves after the navigation, this cleanup will never run.
      console.log(`Auth0PostLoginHandlerHelper => "${theHandler.name}" redirects the user off the login domain; applying cross-subdomain logout safety...`);
      theHandler._applyCrossSubdomainLogoutSafety();
    }
  };
}

export default Auth0PostLoginHandlerHelper;
